/**
 * Number and date formatting. The KPI formatters mirror `crates/core/src/format.rs` so that
 * locally formatted numbers (chart axes, totals, mock data) read exactly like the API's
 * `formatted_value`: `$84.3K`, `184K`, `1.35%`, `1m 34s`, `42K hrs`.
 */
import type { KpiUnit } from '@/api/types';

function trimDecimals(v: number, decimals: number): string {
  const s = v.toFixed(decimals);
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

function groupThousands(v: number, decimals: number): string {
  const s = v.toFixed(decimals);
  const [int = '0', fracRaw] = s.split('.');
  const frac = fracRaw?.replace(/0+$/, '');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${grouped}.${frac}` : grouped;
}

/** `1234` → `1,234`; `18400` → `18.4K`; `1200000` → `1.2M`. */
export function compact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  let scaled: number;
  let suffix: string;
  if (abs >= 1e9) [scaled, suffix] = [abs / 1e9, 'B'];
  else if (abs >= 1e6) [scaled, suffix] = [abs / 1e6, 'M'];
  else if (abs >= 1e4) [scaled, suffix] = [abs / 1e3, 'K'];
  else return `${sign}${groupThousands(abs, abs < 100 && abs % 1 !== 0 ? 2 : 0)}`;
  return `${sign}${trimDecimals(scaled, scaled >= 100 ? 0 : 1)}${suffix}`;
}

export function currencyPrefix(code: string | null | undefined): string {
  switch (code ?? 'USD') {
    case 'USD':
    case 'CAD':
    case 'AUD':
    case 'NZD':
    case 'MXN':
      return '$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'JPY':
    case 'CNY':
      return '¥';
    case 'INR':
      return '₹';
    default:
      return `${code} `;
  }
}

export function money(value: number, currency?: string | null): string {
  const prefix = currencyPrefix(currency);
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e4) return `${sign}${prefix}${compact(abs)}`;
  if (abs >= 1e3) return `${sign}${prefix}${trimDecimals(abs / 1e3, 1)}K`;
  return `${sign}${prefix}${groupThousands(abs, abs < 100 ? 2 : 0)}`;
}

/** Seconds → `1m 34s`, `2h 05m`, `45s`. */
export function duration(seconds: number): string {
  const s = Math.round(Math.max(0, seconds));
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
  if (s >= 60) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${s}s`;
}

export function percent(value: number): string {
  return `${trimDecimals(value, Math.abs(value) >= 10 ? 1 : 2)}%`;
}

/** `+7.2%`, `-2%`, `0%`. */
export function change(pct: number): string {
  const body = trimDecimals(Math.abs(pct), Math.abs(pct) >= 100 ? 0 : 1);
  if (pct > 0) return `+${body}%`;
  if (pct < 0) return `-${body}%`;
  return '0%';
}

/** Change without the sign (the arrow carries the direction): `3.4%`. */
export function changeAbs(pct: number): string {
  return `${trimDecimals(Math.abs(pct), Math.abs(pct) >= 100 ? 0 : 1)}%`;
}

export function formatKpi(
  value: number | null | undefined,
  unit: KpiUnit,
  currency?: string | null,
  text?: string | null,
): string {
  if (unit === 'text' && text) return text;
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (unit) {
    case 'count':
      return compact(value);
    case 'currency':
      return money(value, currency);
    case 'percent':
      return percent(value);
    case 'duration':
      return duration(value);
    case 'hours':
      return `${compact(value)} hrs`;
    case 'text':
      return compact(value);
  }
}

/** Shorter labels for chart axes. */
export function formatAxis(value: number, unit: KpiUnit, currency?: string | null): string {
  if (unit === 'duration') return value >= 60 ? `${Math.round(value / 60)}m` : `${Math.round(value)}s`;
  if (unit === 'percent') return `${trimDecimals(value, 1)}%`;
  if (unit === 'currency') return money(value, currency).replace(/\.\d+(?=$)/, '');
  if (unit === 'hours') return `${compact(value)}h`;
  return compact(value);
}

// ------------------------------------------------------------------ dates

/** Parse a `YYYY-MM-DD` date as a local calendar date (not UTC midnight). */
export function parseDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function formatDay(day: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }): string {
  return new Intl.DateTimeFormat(undefined, opts).format(parseDay(day));
}

/** `Oct 14 – Oct 20`, or `Oct 20` for a single day. */
export function formatPeriod(start: string, end: string): string {
  if (start === end) return formatDay(start, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${formatDay(start)} – ${formatDay(end)}`;
}

export function formatDateTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  if (timeZone) opts.timeZone = timeZone;
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: undefined }).format(new Date(iso));
  }
}

/** `just now`, `5 min ago`, `3 h ago`, `2 days ago`, or the date. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const diff = Math.round((now.getTime() - then) / 1000);
  const future = diff < 0;
  const s = Math.abs(diff);
  const say = (n: number, unit: string) => (future ? `in ${n} ${unit}` : `${n} ${unit} ago`);
  if (s < 45) return future ? 'in a moment' : 'just now';
  if (s < 3600) return say(Math.max(1, Math.round(s / 60)), 'min');
  if (s < 86400) return say(Math.round(s / 3600), 'h');
  const days = Math.round(s / 86400);
  if (days < 7) return say(days, days === 1 ? 'day' : 'days');
  return formatDateTime(iso);
}

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
